
/**
 * Dashboard Global Store
 * React Context for shared state: auth, session, risk regime, cycle state,
 * real-time prices, WS connection.
 */

import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { api, authService, dashboardSocket, autoTraderSocket, registerPush, isCapacitor } from '../services';
import type { BOCState, RiskRegime, SessionState, AccountStatus, Setup } from '../services/api';
import type { MT5State, ScanUpdate, CycleState } from '../services/realtime';
import { reportClientDiagnostic } from '../services/clientDiagnostics';
import { isPatternLibraryRouteActive } from './routeGuards';

function setupMergeKey(setup: any): string {
  if (!setup || typeof setup !== 'object') return 'unknown-setup';
  if (setup.id != null) return String(setup.id);
  const pair = String(setup.pair ?? setup.symbol ?? 'UNKNOWN');
  const ts = String(setup.timestamp ?? setup.ts ?? 'NO_TS');
  const cat = String(setup.setup_category ?? setup.setup_type ?? 'NO_CAT');
  return `${pair}-${ts}-${cat}`;
}

function mergeIncomingSetups(_prev: Setup[], incoming: Setup[], _fadeReason: string): Setup[] {
  // The backend already handles same-day setup stickiness via
  // SetupMemory hydration in MultiPairScanner.get_top_setups().
  // Client-side accumulation of "sticky" setups from previous pushes
  // was causing duplicates to balloon because id mismatches let the
  // same logical setup appear multiple times.  Replace rather than merge.
  if (!Array.isArray(incoming) || incoming.length === 0) return _prev;
  return incoming;
}

// `pushed`: the state arrived as a server push (mt5:state), not the 15 s poll.
// `last_push_at` is what the overview's Live marker reads, so a poll can never
// make an account look live.
function toLiveAccountStatus(mt5State: MT5State, prevAccount: AccountStatus | null, pushed = false): AccountStatus | null {
  const rawAccount = (mt5State as any)?.account_info ?? mt5State?.account ?? null;
  const accountInfo = rawAccount && typeof rawAccount === 'object' && Object.keys(rawAccount).length > 0 && !rawAccount.error
    ? rawAccount
    : null;

  if (!accountInfo) {
    if (!prevAccount) return null;
    return {
      ...prevAccount,
      open_trades: 0,
      open_positions: 0,
      mt5_connected: mt5State?.connected ?? false,
      ea_connected: mt5State?.ea_connected ?? false,
      last_mt5_update: mt5State?.timestamp,
    } as AccountStatus;
  }

  const positions = Array.isArray(mt5State?.positions) ? mt5State.positions : [];
  const openPL = positions.reduce((sum: number, position: any) => sum + (Number(position?.profit) || 0), 0);
  const balance = Number(accountInfo.balance) || 0;
  const equity = Number(accountInfo.equity) || balance;
  const freeMargin = Number(accountInfo.free_margin) || Number(accountInfo.margin_free) || 0;
  const profit = Number(accountInfo.profit);

  return {
    ...(prevAccount ?? {}),
    balance,
    equity,
    daily_pnl: positions.length > 0 ? openPL : (Number.isFinite(profit) ? profit : 0),
    risk_per_trade: Number((prevAccount as any)?.risk_per_trade) || 0.01,
    max_drawdown: Number((prevAccount as any)?.max_drawdown) || 0.02,
    open_trades: positions.length,
    open_positions: positions.length,
    free_margin: freeMargin,
    margin: Number(accountInfo.margin) || 0,
    broker: accountInfo.broker ?? accountInfo.company ?? (prevAccount as any)?.broker,
    platform: accountInfo.platform ?? (prevAccount as any)?.platform,
    currency: accountInfo.currency ?? (prevAccount as any)?.currency ?? null,
    mt5_connected: mt5State?.connected ?? false,
    ea_connected: mt5State?.ea_connected ?? false,
    last_mt5_update: mt5State?.timestamp,
    open_pl: openPL,
    live_source: mt5State?.source ?? (prevAccount as any)?.live_source ?? null,
    live_stale: Boolean(mt5State?.stale),
    last_push_at: pushed ? (mt5State?.timestamp ?? new Date().toISOString()) : (prevAccount as any)?.last_push_at,
  } as AccountStatus;
}

function combineAccounts(accountsById: Record<string, AccountStatus>): AccountStatus | null {
  const entries = Object.values(accountsById).filter(Boolean) as AccountStatus[];
  if (entries.length === 0) return null;
  if (entries.length === 1) return entries[0];

  // Aggregate: sum monetary fields, OR-combine booleans, take max timestamp.
  const sumField = (k: keyof AccountStatus) =>
    entries.reduce((total, a) => total + (Number((a as any)?.[k]) || 0), 0);

  // For risk_per_trade / max_drawdown: use the first account's values as the
  // portfolio-wide risk profile isn't meaningful as a sum; a drill-down view
  // is the right place to see per-account risk settings.
  const first = entries[0];

  const latestUpdate = entries
    .map((a) => (a as any)?.last_mt5_update as string | undefined)
    .filter(Boolean)
    .sort()
    .pop();

  // Never add money across currencies (GBP + USD is not a balance): when the
  // accounts disagree, the money fields are null and the header shows "—".
  const currencies = new Set(entries.map((a) => (a as any)?.currency).filter(Boolean));
  const mixed = currencies.size > 1;
  const money = (k: keyof AccountStatus) => (mixed ? null : sumField(k));

  return {
    ...first,
    currency: mixed ? null : ((first as any)?.currency ?? [...currencies][0] ?? null),
    balance: money('balance'),
    equity: money('equity'),
    daily_pnl: money('daily_pnl'),
    free_margin: money('free_margin'),
    margin: money('margin'),
    open_trades: sumField('open_trades'),
    open_positions: sumField('open_positions'),
    mt5_connected: entries.some((a) => (a as any)?.mt5_connected),
    ea_connected: entries.some((a) => (a as any)?.ea_connected),
    last_mt5_update: latestUpdate ?? (first as any)?.last_mt5_update,
    broker: entries.length > 1 ? 'Combined' : (first as any)?.broker,
  } as AccountStatus;
}

// ─── Account scope (Journal feed filter, global) ────────────────────────────
//
// Drives the TopNav "account scope" dropdown and every page that consumes the
// pattern feed (Journal Overview/Trades/Analytics/ML).  Persisted to
// localStorage so the selection survives reloads.

export type AccountScopeMode = 'all' | 'autotrader';

export interface AccountScope {
  mode: AccountScopeMode;
  accountId: string | null;
}

const ACCOUNT_SCOPE_STORAGE_KEY = 'bocapital.accountScope';

function loadAccountScope(): AccountScope {
  if (typeof window === 'undefined') return { mode: 'all', accountId: null };
  try {
    const raw = window.localStorage.getItem(ACCOUNT_SCOPE_STORAGE_KEY);
    if (!raw) return { mode: 'all', accountId: null };
    const parsed = JSON.parse(raw);
    const mode: AccountScopeMode = parsed?.mode === 'autotrader' ? 'autotrader' : 'all';
    const accountId = typeof parsed?.accountId === 'string' ? parsed.accountId : null;
    return { mode, accountId };
  } catch {
    return { mode: 'all', accountId: null };
  }
}

function persistAccountScope(scope: AccountScope): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ACCOUNT_SCOPE_STORAGE_KEY, JSON.stringify(scope));
  } catch {
    /* quota / disabled storage — ignore */
  }
}

// ─── State Shape ────────────────────────────────────────────────────────────

export interface AppState {
  // Auth
  isAuthenticated: boolean;
  username: string | null;
  role: string | null;
  capabilitySet: string[];

  // Connection
  wsConnected: boolean;
  dashboardConnected: boolean;
  autotraderConnected: boolean;
  apiHealthy: boolean;

  // BOCAPITAL
  session: SessionState | null;
  riskRegime: RiskRegime | null;
  bocState: Record<string, BOCState>; // keyed by symbol
  setups: Setup[];

  // Account
  //
  // `account` is the *combined* (summed) view across every connected
  // MT5 bridge account, so dashboard KPIs can show a single portfolio-level
  // balance/P&L without flipping between individual accounts.  `accountsById`
  // holds the raw per-account state keyed by slug (e.g. 'default', 'ftmo_10k')
  // for views that need to drill down to one account at a time.
  account: AccountStatus | null;
  accountsById: Record<string, AccountStatus>;
  dailyPnl: number;

  // Calendar / Market
  economicCalendar: any[];
  fearGreed: any | null;
  weeklyShortlist: string[];

  // Market status
  marketClosed: boolean;

  // Global account scope (Journal filter + anywhere else that consumes the
  // pattern feed). Persisted to localStorage.
  accountScope: AccountScope;

  // UI
  sidebarCollapsed: boolean;
  sidebarMobileOpen: boolean;
  activeView: string;

  // Loading
  loading: Record<string, boolean>;
  errors: Record<string, string>;
}

const initialState: AppState = {
  isAuthenticated: authService.isAuthenticated(),
  username: authService.getUsername(),
  role: authService.getRole(),
  capabilitySet: authService.getCapabilities(),
  wsConnected: false,
  dashboardConnected: false,
  autotraderConnected: false,
  apiHealthy: true,
  session: null,
  riskRegime: null,
  bocState: {},
  setups: [],
  account: null,
  accountsById: {},
  dailyPnl: 0,
  economicCalendar: [],
  fearGreed: null,
  weeklyShortlist: [],
  marketClosed: false,
  accountScope: loadAccountScope(),
  sidebarCollapsed: false,
  sidebarMobileOpen: false,
  activeView: 'command-center',
  loading: {},
  errors: {},
};

// ─── Actions ────────────────────────────────────────────────────────────────

type Action =
  | {
      type: 'SET_AUTH';
      payload: {
        isAuthenticated: boolean;
        username: string | null;
        role: string | null;
        capabilitySet: string[];
      };
    }
  | { type: 'SET_WS_CONNECTED'; payload: boolean }
  | { type: 'SET_DASHBOARD_CONNECTED'; payload: boolean }
  | { type: 'SET_AUTOTRADER_CONNECTED'; payload: boolean }
  | { type: 'SET_API_HEALTHY'; payload: boolean }
  | { type: 'SET_SESSION'; payload: SessionState }
  | { type: 'SET_RISK_REGIME'; payload: RiskRegime }
  | { type: 'SET_BOC_STATE'; payload: BOCState }
  | { type: 'SET_SETUPS'; payload: Setup[] }
  | { type: 'SET_MARKET_CLOSED'; payload: boolean }
  | { type: 'SET_ACCOUNT'; payload: AccountStatus }
  | { type: 'SET_ACCOUNT_FOR_ID'; payload: { accountId: string; account: AccountStatus } }
  | { type: 'SET_DAILY_PNL'; payload: number }
  | { type: 'SET_ECONOMIC_CALENDAR'; payload: any[] }
  | { type: 'SET_FEAR_GREED'; payload: any }
  | { type: 'SET_WEEKLY_SHORTLIST'; payload: string[] }
  | { type: 'SET_ACCOUNT_SCOPE'; payload: AccountScope }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR_MOBILE'; payload: boolean }
  | { type: 'SET_ACTIVE_VIEW'; payload: string }
  | { type: 'SET_LOADING'; payload: { key: string; value: boolean } }
  | { type: 'SET_ERROR'; payload: { key: string; value: string | null } };

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_AUTH':
      return { ...state, ...action.payload };
    case 'SET_WS_CONNECTED':
      return { ...state, wsConnected: action.payload };
    case 'SET_DASHBOARD_CONNECTED':
      return { ...state, dashboardConnected: action.payload };
    case 'SET_AUTOTRADER_CONNECTED':
      return { ...state, autotraderConnected: action.payload };
    case 'SET_API_HEALTHY':
      return { ...state, apiHealthy: action.payload };
    case 'SET_SESSION':
      return { ...state, session: action.payload };
    case 'SET_RISK_REGIME':
      return { ...state, riskRegime: action.payload };
    case 'SET_BOC_STATE':
      return {
        ...state,
        bocState: { ...state.bocState, [action.payload.symbol]: action.payload },
      };
    case 'SET_SETUPS':
      return { ...state, setups: Array.isArray(action.payload) ? action.payload : state.setups };
    case 'SET_MARKET_CLOSED':
      return { ...state, marketClosed: action.payload };
    case 'SET_ACCOUNT': {
      // Once the per-account stream has populated `accountsById`, the combined
      // view is authoritative — drop any single-account writes (from HTTP
      // polling or the legacy single-bridge socket) so the header/sidebar
      // can't flicker between summed and single-account values.
      if (Object.keys(state.accountsById).length > 0) {
        return state;
      }
      return {
        ...state,
        account: action.payload,
        dailyPnl: action.payload?.daily_pnl ?? (action.payload as any)?.dailyPnl ?? state.dailyPnl,
      };
    }
    case 'SET_ACCOUNT_FOR_ID': {
      const { accountId, account } = action.payload;
      const nextAccountsById = { ...state.accountsById, [accountId]: account };
      const combined = combineAccounts(nextAccountsById);
      return {
        ...state,
        accountsById: nextAccountsById,
        account: combined,
        dailyPnl: combined?.daily_pnl ?? state.dailyPnl,
      };
    }
    case 'SET_DAILY_PNL':
      return { ...state, dailyPnl: action.payload };
    case 'SET_ECONOMIC_CALENDAR':
      return { ...state, economicCalendar: action.payload };
    case 'SET_FEAR_GREED':
      return { ...state, fearGreed: action.payload };
    case 'SET_WEEKLY_SHORTLIST':
      return { ...state, weeklyShortlist: action.payload };
    case 'SET_ACCOUNT_SCOPE': {
      const next: AccountScope = {
        mode: action.payload.mode === 'autotrader' ? 'autotrader' : 'all',
        accountId: action.payload.mode === 'autotrader' ? (action.payload.accountId || null) : null,
      };
      persistAccountScope(next);
      return { ...state, accountScope: next };
    }
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
    case 'SET_SIDEBAR_MOBILE':
      return { ...state, sidebarMobileOpen: action.payload };
    case 'SET_ACTIVE_VIEW':
      return { ...state, activeView: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: { ...state.loading, [action.payload.key]: action.payload.value } };
    case 'SET_ERROR':
      const errs = { ...state.errors };
      if (action.payload.value) errs[action.payload.key] = action.payload.value;
      else delete errs[action.payload.key];
      return { ...state, errors: errs };
    default:
      return state;
  }
}

// ─── Context ────────────────────────────────────────────────────────────────

interface StoreCtx {
  state: AppState;
  dispatch: React.Dispatch<Action>;
  actions: ReturnType<typeof createActions>;
}

interface UiState {
  sidebarCollapsed: boolean;
  sidebarMobileOpen: boolean;
  activeView: string;
}

interface AuthState {
  isAuthenticated: boolean;
  username: string | null;
  role: string | null;
  capabilitySet: string[];
}

interface ConnectionState {
  wsConnected: boolean;
  dashboardConnected: boolean;
  autotraderConnected: boolean;
}

interface AccountState {
  account: AccountStatus | null;
  accountsById: Record<string, AccountStatus>;
  dailyPnl: number;
}

interface SetupsState {
  setups: Setup[];
}

interface ShortlistState {
  weeklyShortlist: string[];
}

interface AccountScopeState {
  accountScope: AccountScope;
}

const StoreStateContext = createContext<AppState | null>(null);
const StoreDispatchContext = createContext<React.Dispatch<Action> | null>(null);
const StoreActionsContext = createContext<ReturnType<typeof createActions> | null>(null);
const UiStateContext = createContext<UiState | null>(null);
const AuthStateContext = createContext<AuthState | null>(null);
const ConnectionStateContext = createContext<ConnectionState | null>(null);
const AccountStateContext = createContext<AccountState | null>(null);
const SetupsStateContext = createContext<SetupsState | null>(null);
const ShortlistStateContext = createContext<ShortlistState | null>(null);
const AccountScopeStateContext = createContext<AccountScopeState | null>(null);

function createActions(dispatch: React.Dispatch<Action>) {
  return {
    setAuth: (
      isAuthenticated: boolean,
      username: string | null,
      role: string | null,
      capabilitySet: string[] = [],
    ) => dispatch({ type: 'SET_AUTH', payload: { isAuthenticated, username, role, capabilitySet } }),
    setSession: (s: SessionState) => dispatch({ type: 'SET_SESSION', payload: s }),
    setRiskRegime: (r: RiskRegime) => dispatch({ type: 'SET_RISK_REGIME', payload: r }),
    setBocState: (s: BOCState) => dispatch({ type: 'SET_BOC_STATE', payload: s }),
    setSetups: (s: Setup[]) => dispatch({ type: 'SET_SETUPS', payload: s }),
    setAccount: (a: AccountStatus) => dispatch({ type: 'SET_ACCOUNT', payload: a }),
    setDailyPnl: (v: number) => dispatch({ type: 'SET_DAILY_PNL', payload: v }),
    setCalendar: (c: any[]) => dispatch({ type: 'SET_ECONOMIC_CALENDAR', payload: c }),
    setFearGreed: (f: any) => dispatch({ type: 'SET_FEAR_GREED', payload: f }),
    setShortlist: (s: string[]) => dispatch({ type: 'SET_WEEKLY_SHORTLIST', payload: s }),
    setAccountScope: (scope: AccountScope) => dispatch({ type: 'SET_ACCOUNT_SCOPE', payload: scope }),
    toggleSidebar: () => dispatch({ type: 'TOGGLE_SIDEBAR' }),
    setSidebarMobile: (open: boolean) => dispatch({ type: 'SET_SIDEBAR_MOBILE', payload: open }),
    setActiveView: (v: string) => dispatch({ type: 'SET_ACTIVE_VIEW', payload: v }),
    setLoading: (key: string, value: boolean) => dispatch({ type: 'SET_LOADING', payload: { key, value } }),
    setError: (key: string, value: string | null) => dispatch({ type: 'SET_ERROR', payload: { key, value } }),

    // Auth actions
    login: async (username: string, password: string, remember = false) => {
      const result = await authService.login(username, password, remember);
      if (result.success) {
        dispatch({
          type: 'SET_AUTH',
          payload: {
            isAuthenticated: true,
            username: authService.getUsername(),
            role: authService.getRole(),
            capabilitySet: authService.getCapabilities(),
          },
        });
      }
      return result;
    },
    logout: () => {
      authService.logout();
      dispatch({
        type: 'SET_AUTH',
        payload: { isAuthenticated: false, username: null, role: null, capabilitySet: [] },
      });
    },
  };
}

// ─── Provider ───────────────────────────────────────────────────────────────

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const actions = React.useMemo(() => createActions(dispatch), [dispatch]);

  // Re-derive role + capabilities from the server on boot.
  //
  // `initialState` seeds them from localStorage, which the user can edit — so
  // the first paint is a guess. This corrects it against the token claim as
  // soon as the network allows. A failed probe deliberately changes nothing:
  // logging the operator out because a request blipped would be a worse
  // failure than briefly trusting a cached role.
  React.useEffect(() => {
    if (!state.isAuthenticated) return;
    let cancelled = false;
    void authService.revalidateSession().then((session) => {
      if (cancelled || !session) return;
      dispatch({
        type: 'SET_AUTH',
        payload: {
          isAuthenticated: true,
          username: authService.getUsername(),
          role: session.role,
          capabilitySet: session.capabilities,
        },
      });
    });
    return () => { cancelled = true; };
    // Runs on mount and on any authentication transition (login/logout).
  }, [state.isAuthenticated]);
  const authState = React.useMemo<AuthState>(
    () => ({
      isAuthenticated: state.isAuthenticated,
      username: state.username,
      role: state.role,
      capabilitySet: state.capabilitySet,
    }),
    [state.isAuthenticated, state.username, state.role, state.capabilitySet],
  );
  const uiState = React.useMemo<UiState>(
    () => ({
      sidebarCollapsed: state.sidebarCollapsed,
      sidebarMobileOpen: state.sidebarMobileOpen,
      activeView: state.activeView,
    }),
    [state.sidebarCollapsed, state.sidebarMobileOpen, state.activeView],
  );
  const connectionState = React.useMemo<ConnectionState>(
    () => ({
      wsConnected: state.wsConnected,
      dashboardConnected: state.dashboardConnected,
      autotraderConnected: state.autotraderConnected,
    }),
    [state.wsConnected, state.dashboardConnected, state.autotraderConnected],
  );
  const accountState = React.useMemo<AccountState>(
    () => ({
      account: state.account,
      accountsById: state.accountsById,
      dailyPnl: state.dailyPnl,
    }),
    [state.account, state.accountsById, state.dailyPnl],
  );
  const setupsState = React.useMemo<SetupsState>(
    () => ({ setups: state.setups }),
    [state.setups],
  );
  const shortlistState = React.useMemo<ShortlistState>(
    () => ({ weeklyShortlist: state.weeklyShortlist }),
    [state.weeklyShortlist],
  );
  const accountScopeState = React.useMemo<AccountScopeState>(
    () => ({ accountScope: state.accountScope }),
    [state.accountScope],
  );
  const accountRef = useRef<AccountStatus | null>(initialState.account);
  const accountsByIdRef = useRef<Record<string, AccountStatus>>(initialState.accountsById);
  // Enabled registry accounts (null until the first /api/accounts read).
  const enabledAccountIdsRef = useRef<Set<string> | null>(null);
  const setupsRef = useRef<Setup[]>(initialState.setups);

  useEffect(() => {
    accountRef.current = state.account;
  }, [state.account]);

  useEffect(() => {
    accountsByIdRef.current = state.accountsById;
  }, [state.accountsById]);

  useEffect(() => {
    setupsRef.current = state.setups;
  }, [state.setups]);

  // Capacitor push registration after auth.  Native /ws handles real-time —
  // the legacy wsManager path is gated off (ENABLE_LEGACY_WS=false) so we
  // only need the Capacitor side-effect here.
  useEffect(() => {
    if (!state.isAuthenticated) return;
    dispatch({ type: 'SET_WS_CONNECTED', payload: false });
    if (isCapacitor) registerPush();
  }, [state.isAuthenticated]);

  // ── Dashboard channel lifecycle (native /ws) ────────────────────────────
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const unsubs: (() => void)[] = [];

    unsubs.push(dashboardSocket.on('connect', () =>
      dispatch({ type: 'SET_DASHBOARD_CONNECTED', payload: true }),
    ));
    unsubs.push(dashboardSocket.on('disconnect', () =>
      dispatch({ type: 'SET_DASHBOARD_CONNECTED', payload: false }),
    ));

    // NB: price:tick / price:snapshot were previously consumed by a
    // PricesProvider that no view actually read from — that subscription
    // was removed to stop ~170 events/sec of pure GC churn.

    // Scan results
    unsubs.push(dashboardSocket.on('scan:update', (data: ScanUpdate) => {
      if (isPatternLibraryRouteActive()) {
        void reportClientDiagnostic('patterns_route_skip_scan_update', {}, 'Skipped scan:update store dispatch on /patterns');
        return;
      }
      // Mirror server-side market_closed flag (set on weekends)
      if (typeof (data as any)?.market_closed === 'boolean') {
        dispatch({ type: 'SET_MARKET_CLOSED', payload: (data as any).market_closed });
      }
      if (data?.top_setups) {
        const incoming = data.top_setups as any as Setup[];
        const merged = mergeIncomingSetups(
          setupsRef.current,
          incoming,
          'not present in latest scan:update',
        );
        dispatch({ type: 'SET_SETUPS', payload: merged });
      }
    }));

    // Setup alerts
    unsubs.push(dashboardSocket.on('setup:alert', (data: any) => {
      if (isPatternLibraryRouteActive()) {
        void reportClientDiagnostic('patterns_route_skip_setup_alert', {}, 'Skipped setup:alert store dispatch on /patterns');
        return;
      }
      if (data?.setups) {
        const incoming = data.setups as Setup[];
        const merged = mergeIncomingSetups(
          setupsRef.current,
          incoming,
          'not present in latest setup:alert',
        );
        dispatch({ type: 'SET_SETUPS', payload: merged });
      }
    }));

    // Cycle state per symbol
    unsubs.push(dashboardSocket.on('cycle:state', (data: CycleState) => {
      if (data?.symbol && data?.state) {
        dispatch({ type: 'SET_BOC_STATE', payload: { symbol: data.symbol, ...data.state } as any });
      }
    }));

    // Session updates
    unsubs.push(dashboardSocket.on('session:update', (data: any) => {
      if (data) dispatch({ type: 'SET_SESSION', payload: data });
    }));

    // KPI real-time updates (~60s from scan loop)
    unsubs.push(dashboardSocket.on('kpi:update', (data: any) => {
      if (isPatternLibraryRouteActive()) {
        void reportClientDiagnostic('patterns_route_skip_kpi_update', {}, 'Skipped kpi:update store dispatch on /patterns');
        return;
      }
      if (!data) return;
      // Account data (from risk engine or MT5).
      // Skip when the per-account autotrader stream is live — its combined
      // view is authoritative and this single-bridge snapshot would stomp it,
      // causing balance to flicker between summed and single-account values.
      const perAccountActive = Object.keys(accountsByIdRef.current).length > 0;
      if (!perAccountActive) {
        if (data.mt5) {
          const nextAccount = toLiveAccountStatus(data.mt5 as MT5State, accountRef.current);
          if (nextAccount) dispatch({ type: 'SET_ACCOUNT', payload: nextAccount });
        } else if (data.account) {
          dispatch({ type: 'SET_ACCOUNT', payload: data.account });
        }
      }
      // Risk regime
      if (data.risk_regime) {
        dispatch({ type: 'SET_RISK_REGIME', payload: data.risk_regime });
      }
      // Fear & Greed
      if (data.fear_greed) {
        const fg = data.fear_greed.payload ?? data.fear_greed;
        dispatch({ type: 'SET_FEAR_GREED', payload: fg });
      }
    }));

    dispatch({ type: 'SET_DASHBOARD_CONNECTED', payload: dashboardSocket.isConnected });
    dashboardSocket.connect();

    return () => {
      unsubs.forEach((fn) => fn());
      dashboardSocket.disconnect();
    };
  }, [state.isAuthenticated]);

  // ── Per-account status feed (always-on) ─────────────────────────────────
  // Only MT5 EA pushes reach the browser as mt5:state, so the cTrader account
  // (master) never entered accountsById: the header showed ftmo2's ~9k and the
  // master balance only flashed from the one-off HTTP load (2026-10-09). Poll
  // /api/mt/status for every enabled account and feed the same per-account view.
  useEffect(() => {
    if (!state.isAuthenticated) return;
    let cancelled = false;
    const POLL_MS = 15_000;

    const tick = async () => {
      try {
        const reg = await api.getAccounts();
        const ids = ((reg as any)?.accounts ?? [])
          .filter((a: any) => a && a.enabled !== false && !a.archived)
          .map((a: any) => String(a.account_id));
        if (cancelled) return;
        enabledAccountIdsRef.current = new Set(ids);
        await Promise.all(ids.map(async (accountId: string) => {
          try {
            const res: any = await api.getMT5Status(accountId);
            const info = res?.info ?? {};
            const mt5State = {
              account: info.account ?? info.account_info ?? null,
              positions: info.positions ?? [],
              connected: Boolean(res?.connected ?? info.connected),
              ea_connected: Boolean(info.ea_connected ?? res?.connected),
              timestamp: new Date().toISOString(),
            } as unknown as MT5State;
            const prev = accountsByIdRef.current[accountId] ?? null;
            const nextAccount = toLiveAccountStatus(mt5State, prev);
            if (!cancelled && nextAccount) {
              dispatch({ type: 'SET_ACCOUNT_FOR_ID', payload: { accountId, account: nextAccount } });
            }
          } catch (err) {
            void reportClientDiagnostic('account_status_poll_failed', { accountId, error: String(err) },
              'per-account status poll failed');
          }
        }));
      } catch (err) {
        void reportClientDiagnostic('account_registry_poll_failed', { error: String(err) },
          'account registry poll failed');
      }
    };

    void tick();
    const timer = setInterval(() => { void tick(); }, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [state.isAuthenticated]);

  // ── Autotrader channel lifecycle (always-on) ────────────────────────────
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const unsubs: (() => void)[] = [];

    unsubs.push(autoTraderSocket.on('connect', () =>
      {
        dispatch({ type: 'SET_AUTOTRADER_CONNECTED', payload: true });
        autoTraderSocket.requestStatus();
      }
    ));
    unsubs.push(autoTraderSocket.on('disconnect', () =>
      dispatch({ type: 'SET_AUTOTRADER_CONNECTED', payload: false }),
    ));
    unsubs.push(autoTraderSocket.on('mt5:state', (data: MT5State) => {
      if (!data) return;
      const accountId = String((data as any)?.account_id || 'default');
      // Only enabled registry accounts count towards the header. The socket's
      // request_status reply is untagged (lands on 'default', the retired MT5
      // slot) and would otherwise sit in the sum (2026-10-09).
      if (enabledAccountIdsRef.current && !enabledAccountIdsRef.current.has(accountId)) return;
      // Compute the per-account snapshot off the *previous* state for this
      // specific account, not the combined view — otherwise every tick would
      // layer summed balances on top of raw per-account numbers.
      const prev = accountsByIdRef.current[accountId] ?? null;
      const nextAccount = toLiveAccountStatus(data, prev, true);
      if (!nextAccount) return;
      dispatch({ type: 'SET_ACCOUNT_FOR_ID', payload: { accountId, account: nextAccount } });
    }));

    dispatch({ type: 'SET_AUTOTRADER_CONNECTED', payload: autoTraderSocket.isConnected });
    autoTraderSocket.connect();
    autoTraderSocket.requestStatus();

    return () => {
      unsubs.forEach((fn) => fn());
      autoTraderSocket.disconnect();
    };
  }, [state.isAuthenticated]);

  // Auth events
  useEffect(() => {
    const onUnauth = () => actions.setAuth(false, null, null, []);
    const onLogout = () => actions.setAuth(false, null, null, []);
    window.addEventListener('bocapital:unauthorized', onUnauth);
    window.addEventListener('bocapital:logout', onLogout);
    return () => {
      window.removeEventListener('bocapital:unauthorized', onUnauth);
      window.removeEventListener('bocapital:logout', onLogout);
    };
  }, [actions]);

  return (
    <StoreDispatchContext.Provider value={dispatch}>
      <StoreActionsContext.Provider value={actions}>
        <AuthStateContext.Provider value={authState}>
          <UiStateContext.Provider value={uiState}>
            <ConnectionStateContext.Provider value={connectionState}>
              <AccountStateContext.Provider value={accountState}>
                <SetupsStateContext.Provider value={setupsState}>
                  <ShortlistStateContext.Provider value={shortlistState}>
                    <AccountScopeStateContext.Provider value={accountScopeState}>
                      <StoreStateContext.Provider value={state}>
                        {children}
                      </StoreStateContext.Provider>
                    </AccountScopeStateContext.Provider>
                  </ShortlistStateContext.Provider>
                </SetupsStateContext.Provider>
              </AccountStateContext.Provider>
            </ConnectionStateContext.Provider>
          </UiStateContext.Provider>
        </AuthStateContext.Provider>
      </StoreActionsContext.Provider>
    </StoreDispatchContext.Provider>
  );
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useStore() {
  const state = useContext(StoreStateContext);
  const dispatch = useContext(StoreDispatchContext);
  const actions = useContext(StoreActionsContext);
  if (!state || !dispatch || !actions) {
    throw new Error('useStore must be inside StoreProvider');
  }
  return { state, dispatch, actions } as StoreCtx;
}

export function useAppState() {
  const state = useContext(StoreStateContext);
  if (!state) throw new Error('useAppState must be inside StoreProvider');
  return state;
}

export function useActions() {
  const actions = useContext(StoreActionsContext);
  if (!actions) throw new Error('useActions must be inside StoreProvider');
  return actions;
}

export function useUiState() {
  const state = useContext(UiStateContext);
  if (!state) throw new Error('useUiState must be inside StoreProvider');
  return state;
}

export function useAuthState() {
  const state = useContext(AuthStateContext);
  if (!state) throw new Error('useAuthState must be inside StoreProvider');
  return state;
}

export function useConnectionState() {
  const state = useContext(ConnectionStateContext);
  if (!state) throw new Error('useConnectionState must be inside StoreProvider');
  return state;
}

export function useAccountState() {
  const state = useContext(AccountStateContext);
  if (!state) throw new Error('useAccountState must be inside StoreProvider');
  return state;
}

export function useSetupsState() {
  const state = useContext(SetupsStateContext);
  if (!state) throw new Error('useSetupsState must be inside StoreProvider');
  return state;
}

export function useShortlistState() {
  const state = useContext(ShortlistStateContext);
  if (!state) throw new Error('useShortlistState must be inside StoreProvider');
  return state;
}

export function useAccountScopeState() {
  const state = useContext(AccountScopeStateContext);
  if (!state) throw new Error('useAccountScopeState must be inside StoreProvider');
  return state;
}

